import { supabase } from "@/integrations/supabase/client";

export interface TxLineInput {
  category_id?: string | null;
  provision_id?: string | null;
  tag_id?: string | null;
  amount: number;
  amount_original?: number | null;
  currency_original?: string | null;
  visibility: "private" | "shared";
  owner_id: string;
}

export interface TxHeaderInput {
  account_id: string;
  date: string;
  total_amount: number;
  description?: string;
  exchange_rate?: number;
  is_refund?: boolean;
  /** Solo `household_income_pool` pasa por `create_transaction` (el aporte a tarjeta va por `create_transfer`). */
  purpose?: "household_income_pool";
  /**
   * R1: compra original a la que este reembolso se vincula. `undefined` (la clave no viene en el
   * objeto) = en `updateTransaction` se conserva el vínculo existente; en `createTransaction` es lo
   * mismo que `null` (sin vínculo). `null` explícito = sin vínculo / desvincular (Decisión 20).
   */
  refund_of_header_id?: string | null;
  /**
   * D1: el texto del banco tal cual, aparte de la `description` que eligió el usuario. Sólo lo escribe
   * `create_transaction` al cargar desde la conciliación de BANCO; `update_transaction` no lo toca.
   */
  bank_description?: string | null;
}

/**
 * D1: `bank_description` del header de un movimiento NUEVO. Editando nunca se manda (el RPC de edición
 * conserva lo guardado) y un texto vacío tampoco: un movimiento cargado a mano no lleva nada.
 */
export function bankDescriptionPatch(input: {
  editing: boolean;
  bankDescription: string | null | undefined;
}): { bank_description?: string } {
  if (input.editing) return {};
  return input.bankDescription ? { bank_description: input.bankDescription } : {};
}

/**
 * D1: el texto del banco que Movimientos muestra en gris debajo de la descripción. `null` = no se muestra:
 * no hay texto, o es igual a la descripción (no se repite), o el header se pinta como "Movimiento privado de
 * <pareja>" (familia 4: el texto del banco dejaría ver el comercio de una línea privada de la pareja).
 */
export function bankDescriptionToShow(input: {
  description: string | null | undefined;
  bankDescription: string | null | undefined;
  isPeerPrivate: boolean;
}): string | null {
  if (input.isPeerPrivate) return null;
  const bank = input.bankDescription?.trim();
  if (!bank) return null;
  if (bank === (input.description ?? "").trim()) return null;
  return bank;
}

function cleanLine(l: TxLineInput) {
  return {
    category_id: l.category_id || null,
    provision_id: l.provision_id || null,
    tag_id: l.tag_id || null,
    amount: l.amount,
    amount_original: l.amount_original ?? null,
    currency_original: l.currency_original ?? null,
    visibility: l.visibility,
    owner_id: l.owner_id,
  };
}

export async function createTransaction(header: TxHeaderInput, lines: TxLineInput[]): Promise<string> {
  const { data, error } = await supabase.rpc("create_transaction", {
    _header: { ...header, type: "regular", exchange_rate: header.exchange_rate ?? 1, is_refund: header.is_refund ?? false } as any,
    _lines: lines.map(cleanLine) as any,
  });
  if (error) throw error;
  return data as string;
}

export async function updateTransaction(headerId: string, header: TxHeaderInput, lines: TxLineInput[]): Promise<string> {
  const { data, error } = await supabase.rpc("update_transaction", {
    _header_id: headerId,
    _header: { ...header, exchange_rate: header.exchange_rate ?? 1, is_refund: header.is_refund ?? false } as any,
    _lines: lines.map(cleanLine) as any,
  });
  if (error) throw error;
  return data as string;
}
